import React, { useState, useRef } from "react";
import {
	Box,
	IconButton,
	TextField,
	Typography,
	LinearProgress,
	Popover,
	InputAdornment,
} from "@mui/material";
import { useTheme } from "@mui/material/styles";
import AttachFileIcon from "@mui/icons-material/AttachFile";
import AddCircleOutlineIcon from "@mui/icons-material/AddCircleOutline";
import SendIcon from "@mui/icons-material/Send";
import CloseIcon from "@mui/icons-material/Close";
import InsertEmoticonIcon from "@mui/icons-material/InsertEmoticon";
import EmojiPicker, { EmojiStyle } from "emoji-picker-react";
import { Conversation } from "@/types/chat";

interface ChatInputAreaProps {
	conversation: Conversation;
	onSendMessage: (
		conversationId: string,
		content: string,
		file: File | null,
		onProgress?: (p: number) => void,
	) => void;
	onTyping: (conversationId: string, isTyping: boolean) => void;
	onPlusClick: (e: React.MouseEvent<HTMLElement>) => void;
}

export const ChatInputArea: React.FC<ChatInputAreaProps> = ({
	conversation,
	onSendMessage,
	onTyping,
	onPlusClick,
}) => {
	const theme = useTheme();
	const [inputText, setInputText] = useState("");
	const [selectedFile, setSelectedFile] = useState<File | null>(null);
	const [uploadProgress, setUploadProgress] = useState<number>(0);
	const [emojiAnchorEl, setEmojiAnchorEl] = useState<null | HTMLElement>(null);
	const fileInputRef = useRef<HTMLInputElement>(null);
	const typingTimeoutRef = useRef<NodeJS.Timeout | null>(null);

	const handleSend = () => {
		if ((!inputText.trim() && !selectedFile) || !conversation) return;

		if (selectedFile) {
			setUploadProgress(1); // Start progress
			onSendMessage(
				conversation.id,
				inputText.trim(),
				selectedFile,
				(progress) => {
					setUploadProgress(progress);
					if (progress === 100) {
						setTimeout(() => setUploadProgress(0), 1000); // hide after a moment
					}
				},
			);
		} else {
			onSendMessage(conversation.id, inputText.trim(), null);
		}

		setInputText("");
		setSelectedFile(null);
		onTyping(conversation.id, false);
	};

	const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
		if (e.target.files && e.target.files.length > 0) {
			setSelectedFile(e.target.files[0]);
		}
		if (fileInputRef.current) fileInputRef.current.value = "";
	};

	const handleKeyDown = (e: React.KeyboardEvent) => {
		if (e.key === "Enter" && !e.shiftKey) {
			e.preventDefault();
			handleSend();
		}
	};

	const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
		setInputText(e.target.value);
		if (conversation) {
			onTyping(conversation.id, true);
			if (typingTimeoutRef.current) clearTimeout(typingTimeoutRef.current);
			typingTimeoutRef.current = setTimeout(() => {
				onTyping(conversation.id, false);
			}, 3000);
		}
	};

	return (
		<>
			{/* Selected File Preview */}
			{selectedFile && (
				<Box
					sx={{
						p: 1,
						px: 2,
						display: "flex",
						alignItems: "center",
						justifyContent: "space-between",
						backgroundColor: "action.hover",
						borderTop: `1px solid ${theme.palette.divider}`,
					}}
				>
					<Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
						<AttachFileIcon fontSize="small" color="action" />
						<Typography variant="body2" noWrap sx={{ maxWidth: 200 }}>
							{selectedFile.name}
						</Typography>
					</Box>
					<IconButton size="small" onClick={() => setSelectedFile(null)}>
						<CloseIcon fontSize="small" />
					</IconButton>
				</Box>
			)}
			{uploadProgress > 0 && uploadProgress < 100 && (
				<LinearProgress
					variant="determinate"
					value={uploadProgress}
					sx={{ height: 2 }}
				/>
			)}

			{/* Input Area */}
			<Box
				sx={{
					p: 1.5,
					borderTop: `1px solid ${theme.palette.mode === "dark" ? "rgba(255,255,255,0.08)" : theme.palette.divider}`,
					backgroundColor:
						theme.palette.mode === "dark"
							? "rgba(0, 0, 0, 0.2)"
							: "background.paper",
					display: "flex",
					gap: 1,
					alignItems: "flex-end", // Align icons to bottom when multiline
				}}
			>
				<IconButton
					color="primary"
					component="label"
					sx={{
						flexShrink: 0,
						mb: 0.5,
						width: inputText.trim() || selectedFile ? 0 : 34,
						opacity: inputText.trim() || selectedFile ? 0 : 1,
						padding: inputText.trim() || selectedFile ? 0 : undefined,
						overflow: "hidden",
						transition: "all 0.3s cubic-bezier(0.4, 0, 0.2, 1)",
						mr: inputText.trim() || selectedFile ? -1 : 0, // counteract parent gap of 1 (8px)
					}}
				>
					<AttachFileIcon fontSize="small" />
					<input
						type="file"
						hidden
						ref={fileInputRef}
						onChange={handleFileChange}
					/>
				</IconButton>
				<IconButton
					color="primary"
					onClick={onPlusClick}
					sx={{ flexShrink: 0, mb: 0.5 }}
				>
					<AddCircleOutlineIcon fontSize="small" />
				</IconButton>
				<TextField
					fullWidth
					multiline
					minRows={1}
					maxRows={4}
					autoComplete="off"
					size="small"
					placeholder="мессеж..."
					variant="outlined"
					value={inputText}
					onChange={handleInputChange}
					onKeyDown={handleKeyDown}
					InputProps={{
						endAdornment: (
							<InputAdornment position="end">
								<IconButton
									size="small"
									onClick={(e) => setEmojiAnchorEl(e.currentTarget)}
									sx={{
										color: theme.palette.text.secondary,
										"&:hover": { color: theme.palette.primary.main },
									}}
								>
									<InsertEmoticonIcon fontSize="small" />
								</IconButton>
							</InputAdornment>
						),
					}}
					sx={{
						"& .MuiOutlinedInput-root": {
							borderRadius: 5,
							backgroundColor:
								theme.palette.mode === "dark"
									? "rgba(255, 255, 255, 0.05)"
									: theme.palette.action.hover,
							"& fieldset": { border: "none" },
							"& input": { color: theme.palette.text.primary },
						},
					}}
				/>
				<IconButton
					color="primary"
					onClick={handleSend}
					disabled={!inputText.trim() && !selectedFile}
					sx={{
						mb: 0.5,
						backgroundColor:
							inputText.trim() || selectedFile
								? theme.palette.mode === "dark"
									? "rgba(25, 118, 210, 0.8)"
									: theme.palette.primary.light
								: "transparent",
						color:
							inputText.trim() || selectedFile
								? "#ffffff"
								: theme.palette.action.disabled,
						"&:hover": {
							backgroundColor:
								theme.palette.mode === "dark"
									? "rgba(25, 118, 210, 1)"
									: theme.palette.primary.main,
						},
					}}
				>
					<SendIcon fontSize="small" />
				</IconButton>
			</Box>

			{/* Emoji Picker Popover */}
			<Popover
				open={Boolean(emojiAnchorEl)}
				anchorEl={emojiAnchorEl}
				onClose={() => setEmojiAnchorEl(null)}
				disableScrollLock={true}
				anchorOrigin={{
					vertical: "top",
					horizontal: "right",
				}}
				transformOrigin={{
					vertical: "bottom",
					horizontal: "right",
				}}
				PaperProps={{
					sx: {
						borderRadius: 3,
						mb: 1,
						boxShadow:
							theme.palette.mode === "dark"
								? "0 8px 32px rgba(0,0,0,0.5)"
								: theme.shadows[12],
					},
				}}
			>
				<EmojiPicker
					emojiStyle={EmojiStyle.NATIVE}
					theme={theme.palette.mode as any}
					onEmojiClick={(emojiData) => {
						setInputText((prev) => prev + emojiData.emoji);
					}}
				/>
			</Popover>
		</>
	);
};
